/**
 * WP-50 — the typed seam onto the shared formula engine.
 *
 * frontend/src/contracts/engine/engine.js is WP-23's byte-identical copy of
 * backend/src/services/contracts/formula/engine.js (plan 6.3, decision D6; a
 * CI test asserts the two are identical). It is plain JS and it is NEVER
 * edited from here — so its surface is declared once, in this file, and every
 * other file in the grid imports the typed names from here.
 *
 * The engine's own contract (its JSDoc) is the authority for these shapes:
 * createWorkbookContext(sheets, options) and the result object of
 * evaluateFormula / evaluateCell.
 */
import * as engineNamespace from '../../engine/engine';

/** An evaluated value. The error box is the engine's own error carrier. */
export type EngineValue = number | string | boolean | { __error: string } | null;

/** The engine's documented result shape (engine.js, "Returns (this is the documented contract)"). */
export interface EngineResult {
  status: 'ok' | 'unsupported' | 'error' | 'not_formula';
  valueType: 'n' | 's' | 'b' | 'e' | 'blank' | null;
  value: EngineValue;
  numberValue: number | null;
  errorValue: string | null;
  unsupported: Array<{ kind: string; name: string | null; detail: string | null }>;
  unsupportedFunctions: string[];
  usedCachedInputs: Array<{ ref: string; reason: string | null }>;
  reason: string | null;
  engineVersion: string;
}

export interface SheetExtent {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

/** What createWorkbookContext returns (engine.js section 7). */
export interface WorkbookContext {
  sheetNames: string[];
  formulaInputs: 'recompute' | 'cached';
  getCell(sheet: string, row: number, col: number): unknown;
  getCellByRef(sheet: string, ref: string): unknown;
  getExtent(sheet: string): SheetExtent | null;
  getValue(sheet: string, row: number, col: number): EngineValue;
  evaluateCell(sheet: string, ref: string): EngineResult;
}

export interface WorkbookContextOptions {
  /** plan 8.1 wants 'recompute': a total is rebuilt from its line items. */
  formulaInputs?: 'recompute' | 'cached';
  definedNames?: Record<string, string>;
  maxRangeCells?: number;
}

/** One token of tokenizeFormula (engine.js section 3). */
export interface FormulaToken {
  type:
    | 'number' | 'string' | 'bool' | 'error' | 'ref' | 'range' | 'name'
    | 'external' | 'sheet3d' | 'op' | 'lparen' | 'rparen' | 'comma';
  value: unknown;
  /** Present on ref / range / name tokens: the sheet prefix, or null. */
  sheet?: string | null;
  sheetQuoted?: boolean;
  start: number;
  end: number;
}

export interface ParsedCellRef {
  sheet: string | null;
  row: number;
  col: number;
  absRow: boolean;
  absCol: boolean;
}

interface EngineSurface {
  createWorkbookContext(
    sheets: Array<{ name: string; cells: unknown[] }>,
    options?: WorkbookContextOptions,
  ): WorkbookContext;
  cellHasFormula(cell: unknown): boolean;
  cellStoredValue(cell: unknown): EngineValue;
  cellCachedValue(cell: unknown): EngineValue | undefined;
  columnIndexToLetter(index: number): string;
  columnLetterToIndex(letters: string): number | null;
  formatCellRef(row: number, col: number): string;
  parseCellRef(text: string): ParsedCellRef | null;
  tokenizeFormula(text: string): FormulaToken[];
  isErrorValue(value: unknown): boolean;
}

/**
 * The one narrowing in the grid. TypeScript infers the JS module loosely
 * (tsconfig allowJs, no checkJs); everything downstream is strict against
 * EngineSurface instead. None of these functions use `this`, so lifting them
 * out of the namespace is safe.
 */
const engine = engineNamespace as unknown as EngineSurface;

export function createWorkbookContext(
  sheets: Array<{ name: string; cells: unknown[] }>,
  options?: WorkbookContextOptions,
): WorkbookContext {
  return engine.createWorkbookContext(sheets, options);
}

export function cellHasFormula(cell: unknown): boolean {
  return engine.cellHasFormula(cell);
}

export function cellStoredValue(cell: unknown): EngineValue {
  return engine.cellStoredValue(cell);
}

export function cellCachedValue(cell: unknown): EngineValue | undefined {
  return engine.cellCachedValue(cell);
}

export function columnIndexToLetter(index: number): string {
  return engine.columnIndexToLetter(index);
}

export function columnLetterToIndex(letters: string): number | null {
  return engine.columnLetterToIndex(letters);
}

export function formatCellRef(row: number, col: number): string {
  return engine.formatCellRef(row, col);
}

export function parseCellRef(text: string): ParsedCellRef | null {
  return engine.parseCellRef(text);
}

export function tokenizeFormula(text: string): FormulaToken[] {
  return engine.tokenizeFormula(text);
}

export function isErrorValue(value: unknown): boolean {
  return engine.isErrorValue(value);
}

/** True when an evaluated value is the engine's error box. */
export function isErrorBox(value: EngineValue): value is { __error: string } {
  return value !== null && typeof value === 'object' && typeof (value as { __error?: unknown }).__error === 'string';
}
